/**
 * Chip Bet Effect for Poker Reborn Effects Engine
 *
 * Animates chips flying from a player's seat to their PlayerPotView.
 * Used for bet, call, raise, allin, and blind posting actions.
 *
 * @see docs/screens/room/CHIPS.md - Flying chips specification
 * @see docs/screens/room/PLAYER_POT.md - Player pot animations
 */

import { Container, Graphics, Sprite } from 'pixi.js';
import { TimedEffect } from '../../core/TimedEffect';
import type { EffectTarget } from '../../core/EffectTypes';
import { EffectPriority } from '../../core/EffectTypes';
import type { EffectOptions } from '../../core/Effect';
import { easeOutCubic } from '../../utils/easing';
import {
  DENOMINATIONS_DESC,
  getVisualChipCount,
  getChipDenominationsForAmount,
  createChipSprite,
  CHIP_FLYING_SCALE,
  getChipFlyingScale,
} from '../../../components/chips/ChipColors';
import { debugOverlay } from '../../../utils/DebugOverlay';

// =============================================================================
// TYPES
// =============================================================================

export interface ChipBetData {
  /** The container to add flying chips to */
  container: Container;
  /** Starting X (seat position) */
  fromX: number;
  /** Starting Y (seat position) */
  fromY: number;
  /** Ending X (player pot position) */
  toX: number;
  /** Ending Y (player pot position) */
  toY: number;
  /** Bet amount */
  amount: number;
  /** Number of chips to animate (auto-calculated if not provided) */
  chipCount?: number | undefined;
  /** Arc height for curved path (default: 20) */
  arcHeight?: number | undefined;
  /** Stagger delay between chips (default: 40ms) */
  stagger?: number | undefined;
  /** Callback when animation completes */
  onComplete?: (() => void) | undefined;
  /** Current screen width for BBS scaling */
  screenWidth?: number | undefined;
}

// =============================================================================
// DEFAULTS
// =============================================================================

const DEFAULT_DURATION = 400; // ms
const DEFAULT_ARC_HEIGHT = 20;
const DEFAULT_STAGGER = 40; // ms
const CHIP_RADIUS = 10;

// =============================================================================
// FLYING CHIP
// =============================================================================

interface FlyingChip {
  displayObject: Sprite | Graphics;
  startX: number;
  startY: number;
  denomination: number;
}

// =============================================================================
// CHIP BET EFFECT
// =============================================================================

/**
 * Animates chips flying from player seat to player pot.
 *
 * Flow:
 * 1. Creates chip graphics at seat position
 * 2. Animates them along arc path to pot position
 * 3. Fades out on arrival
 * 4. Calls onComplete callback
 */
export class ChipBetEffect extends TimedEffect {
  private readonly betData: ChipBetData;
  private chips: FlyingChip[] = [];
  private readonly flyingScale: number;

  constructor(
    target: EffectTarget,
    data: ChipBetData,
    options: EffectOptions = {}
  ) {
    // Capture callbacks to merge with our cleanup
    const userCallbacks = options.callbacks ?? {};
    const userOnComplete = userCallbacks.onComplete;

    super('ChipBet', target, options.duration ?? DEFAULT_DURATION, {
      ...options,
      priority: options.priority ?? EffectPriority.NORMAL,
      callbacks: {
        ...userCallbacks,
        onComplete: () => {
          debugOverlay.log(`🎲 ChipBet DONE: $${data.amount}`);
          // Cleanup flying chips
          this.cleanup();
          // Call data callback
          data.onComplete?.();
          // Call user callback
          userOnComplete?.();
        },
      },
    });

    this.betData = data;
    this.flyingScale = data.screenWidth ? getChipFlyingScale(data.screenWidth) : CHIP_FLYING_SCALE;
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  protected override onPlay(): void {
    const { container, fromX, fromY, toX, toY, amount, chipCount } = this.betData;

    // Determine number of chips based on amount
    const count = chipCount ?? getVisualChipCount(amount);

    debugOverlay.log(`🎲 ChipBet START: $${amount} (${count} chips)`);
    debugOverlay.log(`   from: (${Math.round(fromX)}, ${Math.round(fromY)}) → to: (${Math.round(toX)}, ${Math.round(toY)})`);

    // Get denominations based on amount
    const denominations = getChipDenominationsForAmount(amount, count);

    // Create flying chips at seat position
    this.chips = [];

    for (let i = 0; i < denominations.length; i++) {
      const denomination = denominations[i] ?? 1;

      // Try to create sprite, fall back to graphics
      let displayObject: Sprite | Graphics;
      const sprite = createChipSprite(denomination);
      if (sprite) {
        displayObject = sprite;
      } else {
        // Fallback to graphics
        const graphics = new Graphics();
        const chipColors = this.getChipColors(amount, count);
        this.drawChip(graphics, chipColors[i] ?? 0xcc0000);
        displayObject = graphics;
      }

      // Small random offset for natural look
      const offsetX = (Math.random() - 0.5) * 8;
      const offsetY = (Math.random() - 0.5) * 8;
      const startX = fromX + offsetX;
      const startY = fromY + offsetY;

      displayObject.x = startX;
      displayObject.y = startY;
      displayObject.scale.set(0.5 * this.flyingScale); // Start small

      container.addChild(displayObject);

      this.chips.push({
        displayObject,
        startX,
        startY,
        denomination,
      });
    }

    super.onPlay();
  }

  protected onTick(progress: number): void {
    const { toX, toY, arcHeight, stagger } = this.betData;
    const arc = arcHeight ?? DEFAULT_ARC_HEIGHT;
    const staggerTime = stagger ?? DEFAULT_STAGGER;

    this.chips.forEach((chip, index) => {
      // Calculate staggered progress for this chip
      const staggerOffset = (index * staggerTime) / this.duration;
      const chipProgress = Math.max(
        0,
        Math.min(1, (progress - staggerOffset) / (1 - staggerOffset * this.chips.length / this.duration))
      );

      if (chipProgress <= 0) {
        chip.displayObject.visible = false;
        return;
      }

      chip.displayObject.visible = true;

      // Apply easing
      const eased = easeOutCubic(chipProgress);

      // Interpolate position
      chip.displayObject.x = chip.startX + (toX - chip.startX) * eased;
      chip.displayObject.y = chip.startY + (toY - chip.startY) * eased;

      // Add arc for natural movement (parabolic)
      const arcOffset = -arc * 4 * chipProgress * (1 - chipProgress);
      chip.displayObject.y += arcOffset;

      // Scale: grow in at start, then stay at 1
      if (chipProgress < 0.2) {
        chip.displayObject.scale.set((0.5 + chipProgress * 2.5) * this.flyingScale);
      } else {
        chip.displayObject.scale.set(this.flyingScale);
      }

      // Fade out near end
      if (chipProgress > 0.85) {
        chip.displayObject.alpha = 1 - (chipProgress - 0.85) * 6.67;
      } else {
        chip.displayObject.alpha = 1;
      }
    });
  }

  protected override onCancel(): void {
    super.onCancel();
    this.cleanup();
  }

  // ---------------------------------------------------------------------------
  // Private Methods
  // ---------------------------------------------------------------------------

  private drawChip(graphics: Graphics, color: number): void {
    // Shadow
    graphics.circle(1, 1, CHIP_RADIUS);
    graphics.fill({ color: 0x000000, alpha: 0.3 });

    // Main chip body
    graphics.circle(0, 0, CHIP_RADIUS);
    graphics.fill(color);

    // Edge stripe pattern (2 stripes for small chips)
    const stripeOffset = CHIP_RADIUS * 0.65;
    graphics.circle(-stripeOffset, 0, CHIP_RADIUS * 0.15);
    graphics.fill(0xffffff);
    graphics.circle(stripeOffset, 0, CHIP_RADIUS * 0.15);
    graphics.fill(0xffffff);

    // Border
    graphics.circle(0, 0, CHIP_RADIUS);
    graphics.stroke({ width: 1, color: 0x000000 });

    // Highlight
    graphics.circle(-CHIP_RADIUS * 0.3, -CHIP_RADIUS * 0.3, CHIP_RADIUS * 0.2);
    graphics.fill({ color: 0xffffff, alpha: 0.4 });
  }

  private getChipColors(amount: number, count: number): number[] {
    const colors: number[] = [];

    // Use denominations that make sense for the amount
    let remaining = amount;
    for (const denom of DENOMINATIONS_DESC) {
      while (remaining >= denom.value && colors.length < count) {
        colors.push(denom.color);
        remaining -= denom.value;
      }
    }

    // Fill remaining with lowest denomination
    while (colors.length < count) {
      colors.push(DENOMINATIONS_DESC[DENOMINATIONS_DESC.length - 1]?.color ?? 0xffffff);
    }

    return colors;
  }

  private cleanup(): void {
    const { container } = this.betData;

    for (const chip of this.chips) {
      container.removeChild(chip.displayObject);
      chip.displayObject.destroy();
    }

    this.chips = [];
  }
}

// =============================================================================
// FACTORY FUNCTION
// =============================================================================

/**
 * Create a ChipBetEffect from cue data.
 */
export function createChipBetEffect(
  target: EffectTarget,
  data: Record<string, unknown>,
  options: EffectOptions = {}
): ChipBetEffect | null {
  const container = data['container'];
  if (!(container instanceof Container)) {
    return null;
  }

  const betData: ChipBetData = {
    container,
    fromX: (data['fromX'] as number) ?? 0,
    fromY: (data['fromY'] as number) ?? 0,
    toX: (data['toX'] as number) ?? 0,
    toY: (data['toY'] as number) ?? 0,
    amount: (data['amount'] as number) ?? 0,
    chipCount: data['chipCount'] as number | undefined,
    arcHeight: data['arcHeight'] as number | undefined,
    stagger: data['stagger'] as number | undefined,
    onComplete: data['onComplete'] as (() => void) | undefined,
    screenWidth: data['screenWidth'] as number | undefined,
  };

  return new ChipBetEffect(target, betData, options);
}
